import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {LogicFunctionRegistryService} from '../../../service/api/logic-function-registry.service';
import {
    LogicFunctionDefinitionDto,
    LogicFunctionParameterDto
} from '../../../model/dto/api/logic-function-definition.dto';
import {LogicFunctionDryRunResponseDto} from '../../../model/dto/api/logic-function-dry-run-response.dto';

/**
 * UI-side editor-state for a single Module-Function parameter. The actual
 * script value is derived from this on every serialization pass.
 *
 * @ticket TM-1000001726
 */
interface ParameterEditorState {
    /** Parameter metadata from the function definition. */
    definition: LogicFunctionParameterDto;
    /** Currently selected entry mode. */
    mode: 'literal' | 'variable';
    /** Literal value (string/number/boolean/etc). */
    literalValue: unknown;
    /** Variable name (no `$$` prefix, no braces). */
    variableName: string;
}

/**
 * Dropdown-option for the namespace selector.
 *
 * @ticket TM-1000001726
 */
interface NamespaceOption {
    label: string;
    value: string;
}

/**
 * Structured editor used when the surrounding {@code Logic} uses
 * {@code LogicLanguageEnum.module_function}. Replaces the free-form code editor
 * with a namespace selector, a function selector, a per-parameter editor with
 * literal/variable mode-toggle, a permission hint, and a dry-run button.
 *
 * Script format (Phase 2):
 * <pre>
 * {
 *   "namespace": "ns",
 *   "function":  "sendNotification",
 *   "parameters": {
 *     "notificationTypeAlias": { "literal":  "contractCreated" },
 *     "recipientUserId":       { "from":     "$${customerUserId}" }
 *   },
 *   "exportAlias": "notificationId",
 *   "onError":     "FAIL"
 * }
 * </pre>
 *
 * Inputs:
 *  - {@code script}              – existing JSON script (may be empty).
 *  - {@code variables}           – list of import variable names available for
 *                                  `$${varName}` references.
 *  - {@code permissionConfigJson}– optional JSON from the surrounding
 *                                  {@code LogicFlow.permissionConfigJson} —
 *                                  used to surface a permission hint when the
 *                                  selected function is NOT in
 *                                  {@code allowedFunctions[namespace]}.
 *
 * Outputs:
 *  - {@code scriptChange} – emits the serialized JSON whenever the user
 *                           mutates the form.
 *
 * @ticket TM-1000001726
 */
@Component({
    selector: 'mvs-module-function-script-editor',
    templateUrl: './module-function-script-editor.component.html',
    styleUrls: ['./module-function-script-editor.component.scss'],
    standalone: false
})
export class ModuleFunctionScriptEditorComponent implements OnInit, OnChanges, OnDestroy {

    /** Current JSON-script value (two-way bindable via {@code scriptChange}). */
    @Input() script: string | null | undefined;

    /** Variable names available as `$${varName}` references. */
    @Input() variables: string[] = [];

    /** Optional preset test-values for the dry-run, keyed by variable name. */
    @Input() testVariableValues: Record<string, unknown> = {};

    /**
     * Optional JSON of {@code LogicFlow.permissionConfigJson}. Parsed locally
     * to decide whether the selected function is permitted under the flow's
     * allow-list.
     */
    @Input() permissionConfigJson: string | null | undefined;

    @Output() scriptChange: EventEmitter<string> = new EventEmitter<string>();

    initialized = false;

    /** All known definitions indexed by `${namespace}.${name}`. */
    definitions: LogicFunctionDefinitionDto[] = [];
    private definitionByKey: Map<string, LogicFunctionDefinitionDto> = new Map();

    /** All namespaces extracted from {@link definitions}. */
    namespaceOptions: NamespaceOption[] = [];

    /** Function options filtered to the currently selected namespace. */
    functionOptions: { label: string; value: string }[] = [];

    /** Currently selected namespace (lowercase). */
    selectedNamespace: string | null = null;

    /** Currently selected function name (within {@link selectedNamespace}). */
    selectedFunctionName: string | null = null;

    /** Active definition, if any. */
    activeDefinition: LogicFunctionDefinitionDto | null = null;

    /** Editor-state per parameter, mirrors {@link activeDefinition#parameters}. */
    parameterStates: ParameterEditorState[] = [];

    /** Optional export-alias under which the dispatch result is exported. */
    exportAlias = '';

    /** On-error policy — backend expects "FAIL" or "CONTINUE". */
    onError: 'FAIL' | 'CONTINUE' = 'FAIL';

    /** Available on-error policies for the dropdown. */
    readonly onErrorOptions: { label: string; value: 'FAIL' | 'CONTINUE' }[] = [
        {label: 'Fail (Flow stoppt)', value: 'FAIL'},
        {label: 'Continue (Fehler ignorieren)', value: 'CONTINUE'}
    ];

    /** Mode toggle options for each parameter. */
    readonly modeOptions: { label: string; value: 'literal' | 'variable' }[] = [
        {label: 'Wert', value: 'literal'},
        {label: 'Variable', value: 'variable'}
    ];

    /** Dry-run state. */
    dryRunResult: unknown = null;
    dryRunError: string | null = null;
    dryRunDurationMs: number | null = null;
    dryRunLoading = false;

    /**
     * Parsed `allowedFunctions` map from {@link permissionConfigJson}.
     * Key = namespace, value = set of function names ("*" means wildcard).
     * {@code null} means "no restriction" (default policy).
     */
    private allowedFunctions: Map<string, Set<string>> | null = null;

    private destroy$: Subject<void> = new Subject<void>();

    constructor(private readonly registryService: LogicFunctionRegistryService) {
    }

    ngOnInit(): void {
        this.parsePermissionConfig();
        this.loadDefinitions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['permissionConfigJson']) {
            this.parsePermissionConfig();
        }
        if (changes['script'] && !changes['script'].firstChange) {
            this.hydrateFromScript();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ---------------------------------------------------------------------------------------------
    // Definitions loading
    // ---------------------------------------------------------------------------------------------

    private loadDefinitions(): void {
        this.registryService.listDefinitions()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: defs => this.applyDefinitions(defs ?? []),
                error: () => {
                    this.applyDefinitions([]);
                }
            });
    }

    private applyDefinitions(defs: LogicFunctionDefinitionDto[]): void {
        this.definitions = defs;
        this.definitionByKey = new Map(defs.map(d => [`${d.namespace}.${d.name}`, d]));

        // Build namespace options from distinct namespaces.
        const namespaces = Array.from(new Set(defs.map(d => d.namespace))).sort();
        this.namespaceOptions = namespaces.map(ns => ({label: ns, value: ns}));

        this.hydrateFromScript();
        this.initialized = true;
    }

    // ---------------------------------------------------------------------------------------------
    // Hydration / Serialization
    // ---------------------------------------------------------------------------------------------

    private hydrateFromScript(): void {
        if (!this.script || !this.script.trim()) {
            this.resetSelection();
            return;
        }
        try {
            const parsed = JSON.parse(this.script);
            this.applyParsedScript(parsed);
        } catch {
            this.resetSelection();
        }
    }

    private applyParsedScript(parsed: unknown): void {
        if (!parsed || typeof parsed !== 'object') {
            this.resetSelection();
            return;
        }
        const obj = parsed as {
            namespace?: string;
            function?: string;
            parameters?: Record<string, unknown>;
            exportAlias?: string;
            onError?: string;
        };
        this.selectedNamespace = obj.namespace ?? null;
        this.selectedFunctionName = obj.function ?? null;
        this.exportAlias = obj.exportAlias ?? '';
        this.onError = obj.onError === 'CONTINUE' ? 'CONTINUE' : 'FAIL';

        this.refreshFunctionOptions();

        if (this.selectedNamespace && this.selectedFunctionName) {
            const def = this.definitionByKey.get(`${this.selectedNamespace}.${this.selectedFunctionName}`);
            if (def) {
                this.activeDefinition = def;
                this.parameterStates = def.parameters.map(p =>
                    this.buildParameterState(p, obj.parameters ? obj.parameters[p.name] : undefined)
                );
                return;
            }
        }
        this.activeDefinition = null;
        this.parameterStates = [];
    }

    private buildParameterState(definition: LogicFunctionParameterDto, rawValue: unknown): ParameterEditorState {
        // Phase 2 wraps each parameter into either {literal: ...} or {from: "$${var}"}.
        if (rawValue && typeof rawValue === 'object') {
            const wrapped = rawValue as { literal?: unknown; from?: unknown };
            if (typeof wrapped.from === 'string') {
                const varName = this.extractVariableName(wrapped.from);
                return {
                    definition,
                    mode: 'variable',
                    literalValue: this.defaultLiteralFor(definition),
                    variableName: varName ?? ''
                };
            }
            if ('literal' in wrapped) {
                return {
                    definition,
                    mode: 'literal',
                    literalValue: wrapped.literal ?? this.defaultLiteralFor(definition),
                    variableName: ''
                };
            }
        }
        // Tolerate raw values (legacy/loose form) — treat as literal.
        return {
            definition,
            mode: 'literal',
            literalValue: rawValue !== undefined ? rawValue : this.defaultLiteralFor(definition),
            variableName: ''
        };
    }

    /**
     * Extracts the variable name from a `$${varName}` reference. Returns
     * {@code null} when the value does not match the expected format.
     */
    private extractVariableName(from: string): string | null {
        const match = from.match(/^\$\$\{(.+)\}$/);
        if (match) {
            return match[1];
        }
        // Tolerate plain `$$varName` for backward compatibility.
        if (from.startsWith('$$')) {
            return from.substring(2);
        }
        return null;
    }

    private defaultLiteralFor(definition: LogicFunctionParameterDto): unknown {
        if (definition.defaultValue !== undefined && definition.defaultValue !== null) {
            return definition.defaultValue;
        }
        switch ((definition.type || '').toUpperCase()) {
            case 'NUMBER':
                return null;
            case 'BOOLEAN':
                return false;
            case 'LIST':
                return [];
            case 'MAP':
            case 'OBJECT':
                return {};
            default:
                return '';
        }
    }

    private resetSelection(): void {
        this.selectedNamespace = null;
        this.selectedFunctionName = null;
        this.activeDefinition = null;
        this.parameterStates = [];
        this.exportAlias = '';
        this.onError = 'FAIL';
        this.functionOptions = [];
    }

    private emitScript(): void {
        if (!this.activeDefinition || !this.selectedNamespace || !this.selectedFunctionName) {
            this.scriptChange.emit('');
            return;
        }
        const params: Record<string, unknown> = {};
        for (const state of this.parameterStates) {
            const value = this.serializeState(state);
            if (value !== undefined) {
                params[state.definition.name] = value;
            }
        }
        const node: Record<string, unknown> = {
            namespace: this.selectedNamespace,
            function: this.selectedFunctionName,
            parameters: params
        };
        if (this.exportAlias) {
            node['exportAlias'] = this.exportAlias;
        }
        if (this.onError) {
            node['onError'] = this.onError;
        }
        const json = JSON.stringify(node);
        this.script = json;
        this.scriptChange.emit(json);
    }

    private serializeState(state: ParameterEditorState): unknown {
        if (state.mode === 'variable') {
            return state.variableName ? {from: `$\${${state.variableName}}`} : undefined;
        }
        return {literal: state.literalValue};
    }

    // ---------------------------------------------------------------------------------------------
    // Event handlers (template-bound)
    // ---------------------------------------------------------------------------------------------

    onNamespaceSelected(namespace: string | null): void {
        this.selectedNamespace = namespace;
        this.selectedFunctionName = null;
        this.activeDefinition = null;
        this.parameterStates = [];
        this.refreshFunctionOptions();
        this.emitScript();
    }

    onFunctionSelected(functionName: string | null): void {
        this.selectedFunctionName = functionName;
        if (!functionName || !this.selectedNamespace) {
            this.activeDefinition = null;
            this.parameterStates = [];
            this.emitScript();
            return;
        }
        const def = this.definitionByKey.get(`${this.selectedNamespace}.${functionName}`);
        if (!def) {
            this.activeDefinition = null;
            this.parameterStates = [];
            this.emitScript();
            return;
        }
        this.activeDefinition = def;
        this.parameterStates = def.parameters.map(p =>
            this.buildParameterState(p, this.defaultLiteralFor(p))
        );
        this.emitScript();
    }

    onParameterModeChange(state: ParameterEditorState, mode: 'literal' | 'variable'): void {
        state.mode = mode;
        this.emitScript();
    }

    onLiteralChange(state: ParameterEditorState, value: unknown): void {
        state.literalValue = value;
        this.emitScript();
    }

    onVariableChange(state: ParameterEditorState, name: string): void {
        state.variableName = name;
        this.emitScript();
    }

    onExportAliasChange(alias: string): void {
        this.exportAlias = alias ?? '';
        this.emitScript();
    }

    onErrorPolicyChange(policy: 'FAIL' | 'CONTINUE'): void {
        this.onError = policy;
        this.emitScript();
    }

    onTestVariableValueChange(variableName: string, value: unknown): void {
        this.testVariableValues = {...this.testVariableValues, [variableName]: value};
    }

    private refreshFunctionOptions(): void {
        if (!this.selectedNamespace) {
            this.functionOptions = [];
            return;
        }
        this.functionOptions = this.definitions
            .filter(d => d.namespace === this.selectedNamespace)
            .map(d => ({label: d.label || d.name, value: d.name}))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    // ---------------------------------------------------------------------------------------------
    // Permission Hint
    // ---------------------------------------------------------------------------------------------

    private parsePermissionConfig(): void {
        if (!this.permissionConfigJson || !this.permissionConfigJson.trim()) {
            this.allowedFunctions = null;
            return;
        }
        try {
            const parsed = JSON.parse(this.permissionConfigJson);
            const raw = parsed?.allowedFunctions;
            if (!raw || typeof raw !== 'object') {
                this.allowedFunctions = null;
                return;
            }
            const map = new Map<string, Set<string>>();
            for (const [ns, value] of Object.entries(raw)) {
                if (Array.isArray(value)) {
                    map.set(ns, new Set(value.map(v => String(v))));
                }
            }
            this.allowedFunctions = map;
        } catch {
            this.allowedFunctions = null;
        }
    }

    /**
     * Whether the surrounding flow restricts function calls. Returns
     * {@code false} when there is no permission config or the parsed config
     * does not contain {@code allowedFunctions}.
     */
    get hasPermissionConfig(): boolean {
        return this.allowedFunctions !== null;
    }

    /**
     * True when the surrounding flow has an explicit permission config AND the
     * currently selected function is NOT covered by it.
     */
    get isFunctionBlocked(): boolean {
        if (!this.allowedFunctions || !this.selectedNamespace || !this.selectedFunctionName) {
            return false;
        }
        const wildcardNs = this.allowedFunctions.get('*');
        if (wildcardNs && (wildcardNs.has('*') || wildcardNs.has(this.selectedFunctionName))) {
            return false;
        }
        const nsSet = this.allowedFunctions.get(this.selectedNamespace);
        if (!nsSet) {
            return true;
        }
        return !(nsSet.has('*') || nsSet.has(this.selectedFunctionName));
    }

    // ---------------------------------------------------------------------------------------------
    // Dry-run
    // ---------------------------------------------------------------------------------------------

    runDryRun(): void {
        if (!this.script || !this.activeDefinition) {
            return;
        }
        this.dryRunLoading = true;
        this.dryRunError = null;
        this.dryRunResult = null;
        this.dryRunDurationMs = null;
        this.registryService.dryRun({
            script: this.script,
            variables: this.testVariableValues
        }).subscribe({
            next: (response: LogicFunctionDryRunResponseDto) => {
                this.dryRunResult = response.result ?? null;
                this.dryRunError = response.error ?? null;
                this.dryRunDurationMs = response.durationMs ?? null;
                this.dryRunLoading = false;
            },
            error: err => {
                this.dryRunError = err?.error?.message || err?.message || 'Dry-Run fehlgeschlagen';
                this.dryRunLoading = false;
            }
        });
    }

    /**
     * Returns the list of variable names currently referenced by any parameter.
     * Used to surface test-value inputs for the dry-run.
     */
    get referencedVariableNames(): string[] {
        const names = new Set<string>();
        for (const state of this.parameterStates) {
            if (state.mode === 'variable' && state.variableName) {
                names.add(state.variableName);
            }
        }
        return Array.from(names).sort();
    }

    /**
     * True when every required parameter is populated — controls the dry-run
     * button's disabled state.
     */
    areMandatoryParametersFilled(): boolean {
        if (!this.activeDefinition) {
            return false;
        }
        return this.parameterStates.every(state => this.isStateFilled(state));
    }

    private isStateFilled(state: ParameterEditorState): boolean {
        if (!state.definition.required) {
            return true;
        }
        if (state.mode === 'variable') {
            return !!state.variableName;
        }
        return state.literalValue !== null
            && state.literalValue !== undefined
            && state.literalValue !== '';
    }
}
